import { mapToArr } from '../Common/util';
import { UserDataType } from '../Data/UserData';
import { DEFAULT_SIM_RULES } from './rules';
import {
  Direction,
  LineRuntime,
  PassengerRuntime,
  SimulationState,
  StationRuntime,
  TrainRuntime,
} from './types';

type NetworkModel = {
  stations: Record<number, StationRuntime>;
  stationOrder: number[];
  lines: Record<number, LineRuntime>;
  lineOrder: number[];
  topologyHash: string;
  nextEntityId: number;
};

const hashString = (value: string): string => {
  let hash = 5381;
  for (let i = 0; i < value.length; i += 1) {
    hash = ((hash << 5) + hash) ^ value.charCodeAt(i);
  }
  return (hash >>> 0).toString(36);
};

export const hashTopology = (data: UserDataType): string => {
  const stations = mapToArr(data.stations)
    .map((station) => ({
      stationId: station.stationId,
      position: [Math.round(station.position[0]), Math.round(station.position[1])],
      lineIds: [...station.lineIds].sort((a, b) => a - b),
    }))
    .sort((a, b) => a.stationId - b.stationId);

  const lines = mapToArr(data.lines)
    .map((line) => ({
      lineId: line.lineId,
      order: line.order,
      stationIds: [...line.stationIds],
    }))
    .sort((a, b) => a.lineId - b.lineId);

  return hashString(JSON.stringify({ stations, lines }));
};

export const normalizeLineStops = (
  stationIds: number[]
): { stopIds: number[]; isLoop: boolean } => {
  if (stationIds.length === 0) {
    return { stopIds: [], isLoop: false };
  }

  const collapsed: number[] = [];
  for (const stopId of stationIds) {
    if (collapsed[collapsed.length - 1] !== stopId) {
      collapsed.push(stopId);
    }
  }

  let isLoop = false;
  if (collapsed.length > 2 && collapsed[0] === collapsed[collapsed.length - 1]) {
    isLoop = true;
    collapsed.pop();
  }

  return {
    stopIds: collapsed,
    isLoop,
  };
};

const buildNetworkModelFromUserData = (data: UserDataType): NetworkModel => {
  const stations: Record<number, StationRuntime> = {};
  const stationOrder = mapToArr(data.stations)
    .sort((a, b) => a.stationId - b.stationId)
    .map((station) => station.stationId);

  for (const station of mapToArr(data.stations)) {
    stations[station.stationId] = {
      id: station.stationId,
      position: { x: station.position[0], y: station.position[1] },
      waitingPassengerIds: [],
      connectedLineIds: [],
      overcrowdingTimerSeconds: 0,
      overcrowdingActive: false,
    };
  }

  const lines: Record<number, LineRuntime> = {};
  const lineOrder = mapToArr(data.lines)
    .sort((a, b) => a.order - b.order)
    .map((line) => line.lineId);

  for (const line of mapToArr(data.lines)) {
    const normalized = normalizeLineStops(line.stationIds);
    const stopIds = normalized.stopIds.filter((stationId) => stations[stationId]);
    lines[line.lineId] = {
      id: line.lineId,
      color: line.color,
      stopIds,
      isLoop: normalized.isLoop,
      assignedTrainIds: [],
    };
  }

  for (const lineId of lineOrder) {
    const line = lines[lineId];
    if (!line) {
      continue;
    }

    for (const stationId of line.stopIds) {
      const station = stations[stationId];
      if (!station) {
        continue;
      }
      if (!station.connectedLineIds.includes(line.id)) {
        station.connectedLineIds.push(line.id);
      }
    }
  }

  const maxStationId = stationOrder.reduce((max, id) => Math.max(max, id), 0);
  const maxLineId = lineOrder.reduce((max, id) => Math.max(max, id), 0);

  return {
    stations,
    stationOrder,
    lines,
    lineOrder,
    topologyHash: hashTopology(data),
    nextEntityId: Math.max(maxStationId, maxLineId) + 1,
  };
};

const getLineStopByIndex = (
  line: LineRuntime | undefined,
  index: number
): number | undefined => {
  if (!line) {
    return undefined;
  }
  return line.stopIds[index];
};

const resolveFallbackNextIndex = (
  line: LineRuntime,
  currentStopIndex: number,
  direction: Direction
): number => {
  if (line.stopIds.length <= 1) {
    return currentStopIndex;
  }

  if (line.isLoop) {
    const next = currentStopIndex + (direction === 1 ? 1 : -1);
    return (next + line.stopIds.length) % line.stopIds.length;
  }

  const candidate = currentStopIndex + (direction === 1 ? 1 : -1);
  if (candidate < 0 || candidate >= line.stopIds.length) {
    const opposite = currentStopIndex + (direction === 1 ? -1 : 1);
    if (opposite >= 0 && opposite < line.stopIds.length) {
      return opposite;
    }
    return currentStopIndex;
  }

  return candidate;
};

const remapTrain = (
  train: TrainRuntime,
  oldLine: LineRuntime | undefined,
  nextLine: LineRuntime
): TrainRuntime => {
  const remapped: TrainRuntime = {
    ...train,
    onboardPassengerIds: [],
    segmentProgress: Math.max(0, Math.min(1, train.segmentProgress)),
    waitingAtStationId: undefined,
    manualHold: train.manualHold ?? false,
    scheduledDepartureAtSeconds: train.scheduledDepartureAtSeconds,
  };

  if (nextLine.stopIds.length < 2) {
    remapped.currentStopIndex = 0;
    remapped.nextStopIndex = 0;
    remapped.state = 'Idle';
    remapped.segmentProgress = 0;
    return remapped;
  }

  const currentStopId = getLineStopByIndex(oldLine, train.currentStopIndex);
  const nextStopId = getLineStopByIndex(oldLine, train.nextStopIndex);

  const currentStopIndex =
    currentStopId !== undefined ? nextLine.stopIds.indexOf(currentStopId) : -1;
  const nextStopIndex =
    nextStopId !== undefined ? nextLine.stopIds.indexOf(nextStopId) : -1;

  if (currentStopIndex >= 0) {
    remapped.currentStopIndex = currentStopIndex;
    remapped.nextStopIndex =
      nextStopIndex >= 0 && nextStopIndex !== currentStopIndex
        ? nextStopIndex
        : resolveFallbackNextIndex(nextLine, currentStopIndex, remapped.direction);
    if (remapped.nextStopIndex === remapped.currentStopIndex) {
      remapped.state = 'Idle';
      remapped.segmentProgress = 0;
    }
    return remapped;
  }

  remapped.currentStopIndex = 0;
  remapped.nextStopIndex = 1;
  remapped.direction = 1;
  remapped.segmentProgress = 0;
  remapped.state = 'Travelling';
  remapped.dwellTimeRemainingSeconds = 0;
  return remapped;
};

const getMaxId = (ids: number[]): number =>
  ids.reduce((max, id) => Math.max(max, id), 0);

export const buildSimulationNetworkFromUserData = (
  data: UserDataType,
  seed = Date.now(),
  spareTrains = 2
): SimulationState => {
  const network = buildNetworkModelFromUserData(data);

  return {
    network: {
      stations: network.stations,
      stationOrder: network.stationOrder,
      lines: network.lines,
      lineOrder: network.lineOrder,
      trains: {},
      trainOrder: [],
      spareTrains,
    },
    passenger: {
      passengers: {},
      passengerOrder: [],
    },
    progression: {
      mode: 'Running',
      score: 0,
      deliveredPassengers: 0,
      elapsedSeconds: 0,
      unscaledElapsedSeconds: 0,
    },
    ui: {
      simulationSpeed: 1,
      lastMessage: undefined,
    },
    timers: {
      nextPassengerSpawnAtSeconds: 0.5,
    },
    runtime: {
      routeGraphDirty: true,
      randomSeed: seed >>> 0,
      nextEntityId: network.nextEntityId,
      topologyHash: network.topologyHash,
    },
  };
};

export const hotSyncSimulationWithUserData = (
  state: SimulationState,
  data: UserDataType
): void => {
  const oldLines = state.network.lines;
  const oldStations = state.network.stations;
  const oldTrains = state.network.trains;
  const oldPassengers = state.passenger.passengers;

  const next = buildNetworkModelFromUserData(data);

  for (const stationId of next.stationOrder) {
    const existing = oldStations[stationId];
    if (!existing) {
      continue;
    }
    const station = next.stations[stationId];
    station.overcrowdingActive = existing.overcrowdingActive;
    station.overcrowdingTimerSeconds = existing.overcrowdingTimerSeconds;
  }

  const trains: Record<number, TrainRuntime> = {};
  const trainOrder: number[] = [];

  for (const trainId of state.network.trainOrder) {
    const train = oldTrains[trainId];
    if (!train) {
      continue;
    }

    const nextLine = next.lines[train.assignedLineId];
    if (!nextLine) {
      continue;
    }

    const remapped = remapTrain(train, oldLines[train.assignedLineId], nextLine);
    trains[remapped.id] = remapped;
    trainOrder.push(remapped.id);
    nextLine.assignedTrainIds.push(remapped.id);
  }

  const passengers: Record<number, PassengerRuntime> = {};
  const passengerOrder: number[] = [];

  for (const passengerId of state.passenger.passengerOrder) {
    const passenger = oldPassengers[passengerId];
    if (!passenger) {
      continue;
    }

    if (!next.stations[passenger.destinationStationId]) {
      continue;
    }

    if (passenger.state === 'Waiting') {
      if (
        passenger.currentStationId === undefined ||
        !next.stations[passenger.currentStationId]
      ) {
        continue;
      }

      passengers[passenger.id] = {
        ...passenger,
        state: 'Waiting',
        currentTrainId: undefined,
      };
      passengerOrder.push(passenger.id);
      next.stations[passenger.currentStationId].waitingPassengerIds.push(
        passenger.id
      );
      continue;
    }

    if (
      passenger.state === 'OnTrain' &&
      passenger.currentTrainId !== undefined &&
      trains[passenger.currentTrainId]
    ) {
      passengers[passenger.id] = {
        ...passenger,
        state: 'OnTrain',
        currentStationId: undefined,
      };
      passengerOrder.push(passenger.id);
      trains[passenger.currentTrainId].onboardPassengerIds.push(passenger.id);
    }
  }

  state.network.stations = next.stations;
  state.network.stationOrder = next.stationOrder;
  state.network.lines = next.lines;
  state.network.lineOrder = next.lineOrder;
  state.network.trains = trains;
  state.network.trainOrder = trainOrder;

  state.passenger.passengers = passengers;
  state.passenger.passengerOrder = passengerOrder;

  state.runtime.routeGraphDirty = true;
  state.runtime.topologyHash = next.topologyHash;

  const maxTrainId = getMaxId(trainOrder);
  const maxPassengerId = getMaxId(passengerOrder);
  state.runtime.nextEntityId = Math.max(
    state.runtime.nextEntityId,
    next.nextEntityId,
    maxTrainId + 1,
    maxPassengerId + 1
  );

  state.ui.lastMessage = undefined;

  state.ui.simulationSpeed = Math.max(
    0.5,
    Math.min(state.ui.simulationSpeed, DEFAULT_SIM_RULES.maxSimulationSpeedMultiplier)
  );
};
